import React, { useEffect, useState } from 'react';
import { 
    Grid as MuiGrid, 
    Paper, 
    Typography, 
    Box,
    useTheme,
    useMediaQuery,
    Card,
    CardContent
} from '@mui/material';
import api from '../services/api';

interface DashboardCounts {
    companies: number;
    contacts: number;
    projects: number;
    tasks: number;
}

interface ApiResponse<T> {
    data: T[];
    status: number;
    message?: string;
}

const Dashboard: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [counts, setCounts] = useState<DashboardCounts>({
        companies: 0,
        contacts: 0,
        projects: 0,
        tasks: 0
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const results = await Promise.allSettled([
                    api.get<ApiResponse<any>>('/companies'),
                    api.get<ApiResponse<any>>('/contacts'),
                ]);

                const processedResults = results.map((result) => {
                    if (result.status === 'rejected') {
                        return 0;
                    }
                    const data = result.value?.data;
                    return Array.isArray(data) ? data.length : 0;
                });

                const [companies, contacts, projects, tasks] = processedResults;
                setCounts({ companies, contacts, projects, tasks });
            } catch (err) {
                setError(err instanceof Error ? err.message : 'An error occurred');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    if (error) {
        console.error('Dashboard: Rendering error state:', error);
        return (
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography color="error" variant="h6">
                    Error: {error}
                </Typography>
            </Box>
        );
    }

    const dashboardItems = [
        { title: 'Companies', count: counts.companies, color: theme.palette.primary.main },
        { title: 'Contacts', count: counts.contacts, color: theme.palette.secondary.main },
        { title: 'Projects', count: counts.projects, color: theme.palette.success.main },
        { title: 'Tasks', count: counts.tasks, color: theme.palette.warning.main },
    ];

    return (
        <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
            <Typography 
                variant={isMobile ? "h5" : "h4"} 
                gutterBottom
                sx={{ 
                    mb: { xs: 2, sm: 3 },
                    textAlign: isMobile ? 'center' : 'left'
                }}
            >
                Dashboard
            </Typography>
            
            <MuiGrid container spacing={isMobile ? 2 : 3}>
                {dashboardItems.map((item, index) => (
                    <MuiGrid 
                        key={item.title}
                        item 
                        xs={6} 
                        sm={6} 
                        md={3}
                    >
                        <Card
                            elevation={2}
                            sx={{
                                height: isMobile ? 120 : 140,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    elevation: 4,
                                    transform: 'translateY(-2px)',
                                },
                                '&:active': {
                                    transform: 'translateY(0px)',
                                },
                            }}
                        >
                            <CardContent
                                sx={{
                                    textAlign: 'center',
                                    p: isMobile ? 1.5 : 2,
                                    '&:last-child': {
                                        pb: isMobile ? 1.5 : 2,
                                    },
                                }}
                            >
                                <Typography 
                                    component="h2" 
                                    variant={isMobile ? "subtitle1" : "h6"} 
                                    sx={{ 
                                        color: item.color,
                                        fontWeight: 600,
                                        mb: 1
                                    }}
                                >
                                    {item.title}
                                </Typography>
                                <Typography 
                                    component="p" 
                                    variant={isMobile ? "h5" : "h4"}
                                    sx={{
                                        fontWeight: 'bold',
                                        color: theme.palette.text.primary
                                    }}
                                >
                                    {loading ? '...' : item.count}
                                </Typography>
                            </CardContent>
                        </Card>
                    </MuiGrid>
                ))}
            </MuiGrid>
            
            {/* Welcome message for mobile */}
            {isMobile && (
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                        Welcome to WrkFlowPro! Tap any card above to get started.
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default Dashboard;
